import { AddressDisplay, Typeahead } from '../../gs-component-form';
import { Content, Footer, Header, RouterContext } from '../../gs-component-right-rail';
import { Button } from '@xo-union/tk-component-buttons';
import { Checkbox } from '@xo-union/tk-component-switches';
import { Field as FormikField, FormikProps } from 'formik';
import React, { Fragment, useContext, useState } from 'react';

import { EventType } from '../components/ApiClientsContext';
import { LocationContext } from '../components/LocationContext';
import RightRailSection from '../components/RightRailSection';
import addressComponentParser, { bookingType, LocalVendorValues } from '../helpers/addressComponentHelper';
import hasAddress from '../helpers/addressHelpers';
import resetVendorFields from '../helpers/formHelpers';
import { GoogleAddressValues } from '../helpers/objectifyGoogleAddressComponents';
import { FormValues } from './RightRailForm';

interface Props {
  params?: {
    eventType?: EventType;
    eventIndex?: number;
  };
  formProps: FormikProps<FormValues>;
}

export const SubEventScreen: React.FC<Props> = ({ params: { eventType, eventIndex = 0 } = {}, formProps }) => {
  const [loading, setLoading] = useState(false);
  const { push } = useContext(RouterContext);
  const { setCeremonyAddress, setReceptionAddress, location, deleteBooking } = useContext(LocationContext);

  const onSameClick = ({ target }: React.ChangeEvent<HTMLInputElement>): void => {
    const { location: locationValue } = formProps.values.events[0];
    const { name, street1, city, state, zip, country } = locationValue;

    formProps.setFieldValue('events[0].sameVenue', target.checked);
    if (target.checked) {
      formProps.setFieldValue('events[1].location.name', name);
      formProps.setFieldValue('events[1].location.street1', street1);
      formProps.setFieldValue('events[1].location.city', city);
      formProps.setFieldValue('events[1].location.state', state);
      formProps.setFieldValue('events[1].location.zip', zip);
      formProps.setFieldValue('events[1].location.country', country);
      setReceptionAddress({
        id: location.REC && location.REC.id,
        vendor_name: name,
        address_components: {
          user_entry: {
            street: street1,
            city,
            postal_code: zip,
            state,
            country,
          },
        },
      });
    } else {
      deleteEventLocation(1);
    }
  };

  const setLocationFields = (addressComponentValues: GoogleAddressValues | LocalVendorValues, source: string): void => {
    const { vendor_profile_id, vendor_name, gdsAddress, address_components } = addressComponentParser(
      addressComponentValues,
      source
    );

    formProps.setFieldValue(`events[${eventIndex}].location.zip`, gdsAddress.zip);
    formProps.setFieldValue(`events[${eventIndex}].location.city`, gdsAddress.city);
    formProps.setFieldValue(`events[${eventIndex}].location.state`, gdsAddress.state);
    formProps.setFieldValue(`events[${eventIndex}].location.country`, gdsAddress.country);
    formProps.setFieldValue(`events[${eventIndex}].location.street1`, gdsAddress.street1);
    formProps.setFieldValue(`events[${eventIndex}].location.name`, vendor_name);

    if (eventType === 'Ceremony') {
      setCeremonyAddress({
        id: location.CER && location.CER.id,
        vendor_profile_id,
        vendor_name,
        address_components,
      });
    } else if (eventType === 'Reception') {
      setReceptionAddress({
        id: location.REC && location.REC.id,
        vendor_profile_id,
        vendor_name,
        address_components,
      });
    }
  };

  const deleteEventLocation = async (index: number): Promise<void> => {
    setLoading(true);
    await deleteBooking(eventType);
    setLoading(false);
    resetVendorFields(formProps.setFieldValue, index);
  };

  const editEventLocation = (): void => {
    push({ screen: 'addCustomVendor', params: { eventIndex, eventType } });
  };

  const editLocationCallback = (): undefined | Function => {
    if (!location || !location.CER || bookingType(location.CER.address_components) !== 'user_entry') {
      return undefined;
    }
    return editEventLocation;
  };

  const SameAsCeremony =
    eventType === 'Reception' ? (
      <Checkbox labelDirection="left" checked={formProps.values.events[0].sameVenue} onChange={onSameClick}>
        Same Location as Ceremony
      </Checkbox>
    ) : null;

  const goBack = (): void => push('main');

  return (
    <Fragment>
      <Header leftButtonOnClick={goBack} leftButtonText="Back" />
      <Content>
        <RightRailSection name={`${eventType} Event`} inlineChild={SameAsCeremony}>
          {hasAddress(formProps.values.events[eventIndex].location) ? (
            <AddressDisplay
              vendorType="Venue Name"
              deleteCallback={(): void => {
                deleteEventLocation(eventIndex);
              }}
              editCallback={editLocationCallback()}
              {...formProps.values.events[eventIndex].location}
            />
          ) : (
            <FormikField
              name={`events[${eventIndex}].location.name`}
              label="Venue Name"
              onChange={setLocationFields}
              component={Typeahead}
              onClickAddCustom={(): void => push({ screen: 'addCustomVendor', params: { eventIndex, eventType } })}
              allowCustomVendor
            />
          )}
        </RightRailSection>
      </Content>
      <Footer>
        <Button block onClick={goBack} disabled={loading}>
          Done
        </Button>
      </Footer>
    </Fragment>
  );
};
